import { memo } from "react";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, CheckCircle } from "lucide-react";
import { useSuccessionInsights, type Insight } from "@/hooks/useSuccessionInsights";
import { useNavigate } from "react-router-dom";

interface SuccessionInsightsProps {
  onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  score: "Score Gap",
  assessment: "Assessment Gap",
  team: "Team Gap",
  successor: "Successor Gap",
  task: "Task Alert",
  timeline: "Timeline Alert",
};

const PRIORITY_CONFIG = {
  high: { label: "High Priority", borderColor: "border-l-[#ef4444]", bg: "bg-[#ef4444]/5" },
  medium: { label: "Medium Priority", borderColor: "border-l-amber-500", bg: "bg-amber-500/5" },
  on_track: { label: "On Track", borderColor: "border-l-[#16a34a]", bg: "bg-[#16a34a]/5" },
};

function InsightCard({
  insight,
  onDismiss,
  onNavigate,
}: {
  insight: Insight;
  onDismiss: (key: string) => void;
  onNavigate: (url: string) => void;
}) {
  const config = PRIORITY_CONFIG[insight.priority];
  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-lg border-l-4 ${config.borderColor} ${config.bg} transition-all duration-300`}
    >
      <div className="flex-1 min-w-0">
        <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
          {CATEGORY_LABELS[insight.category] ?? insight.category}
        </span>
        <h4 className="font-medium text-sm text-foreground leading-snug">{insight.headline}</h4>
        <p className="text-xs text-muted-foreground mt-0.5">{insight.detail}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => onNavigate(insight.action_url)}
        >
          Go <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => onDismiss(insight.key)}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

const SuccessionInsights = memo(function SuccessionInsights({ onClose }: SuccessionInsightsProps) {
  const { insights, allInsightsCount, counts, isLoading, dismissInsight } = useSuccessionInsights();
  const navigate = useNavigate();

  const handleNavigate = (url: string) => {
    onClose();
    navigate(url);
  };

  const highInsights = insights.filter((i) => i.priority === "high");
  const medInsights = insights.filter((i) => i.priority === "medium");
  const onTrackInsights = insights.filter((i) => i.priority === "on_track");

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-xl shadow-lg max-w-4xl w-full h-[80vh] overflow-y-auto p-8 relative">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 h-8 w-8 hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </Button>

        <div className="pr-8 mb-2">
          <h1 className="text-2xl font-semibold">Succession Insights</h1>
          <p className="text-muted-foreground">
            {counts.total > 0
              ? `${counts.total} insight${counts.total === 1 ? "" : "s"} identified across your succession plan`
              : "No active insights — your succession plan is on track."}
          </p>
        </div>

        {counts.total > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-6">
            {counts.high > 0 && (
              <span className="rounded-full bg-[#ef4444]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#ef4444]">
                🔴 {counts.high} High priority
              </span>
            )}
            {counts.medium > 0 && (
              <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-600">
                🟡 {counts.medium} Medium priority
              </span>
            )}
            {counts.on_track > 0 && (
              <span className="rounded-full bg-[#16a34a]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#16a34a]">
                🟢 {counts.on_track} On track
              </span>
            )}
          </div>
        )}

        {isLoading && (
          <p className="text-sm text-muted-foreground italic">Loading insights…</p>
        )}

        {!isLoading && counts.total === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle className="h-12 w-12 text-[#16a34a] mb-4" />
            <p className="text-lg font-medium text-foreground">All clear</p>
            <p className="text-sm text-muted-foreground mt-1">
              No active insights — your succession plan is on track.
            </p>
          </div>
        )}

        <div className="space-y-6">
          {highInsights.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#ef4444] mb-3">
                High Priority
              </h3>
              <div className="space-y-2">
                {highInsights.map((i) => (
                  <InsightCard
                    key={i.key}
                    insight={i}
                    onDismiss={dismissInsight}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            </section>
          )}

          {medInsights.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-3">
                Medium Priority
              </h3>
              <div className="space-y-2">
                {medInsights.map((i) => (
                  <InsightCard
                    key={i.key}
                    insight={i}
                    onDismiss={dismissInsight}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            </section>
          )}

          {onTrackInsights.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#16a34a] mb-3">
                On Track
              </h3>
              <div className="space-y-2">
                {onTrackInsights.map((i) => (
                  <InsightCard
                    key={i.key}
                    insight={i}
                    onDismiss={dismissInsight}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            </section>
          )}
          {allInsightsCount > 10 && (
            <p className="text-xs text-muted-foreground text-center mt-4">
              Showing 10 of {allInsightsCount} insights — dismiss some to reveal more
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

SuccessionInsights.displayName = "SuccessionInsights";

export default SuccessionInsights;
